import { useEffect, useMemo, useState } from "react";
import type { PlatformClient } from "../../platform/supabase";
import { CenterReportSubmissionPanel } from "./CenterReportSubmissionPanel";
import { CenterReportAiPanel, type CenterAiSection } from "./CenterAiWriting";

type Tab="PROGRAMS"|"COMPARISON"|"BADGES"|"OFFICIAL";
type ProgramReport={
  summary:{programCount:number;activeProgramCount:number;learnerReach:number;completedParticipationCount:number;verifiedEvidenceCount:number};
  programs:Array<{
    programId:string;title:string;programType:string;status:string;yearCode:string;
    startsOn?:string|null;endsOn?:string|null;targetGradeMin?:number|null;targetGradeMax?:number|null;
    participantCount:number;registrationCount:number;attendedCount:number;completedCount:number;absentCount:number;
    evidenceExpectedCompletedCount:number;evidenceCreatedCount:number;evidenceVerifiedCount:number;
    completionRate:number;evidenceVerificationRate:number;
  }>;
};
type AcademicYear={academicYearId:string;yearCode:string;startsOn:string;endsOn:string;status:string};
type Lifecycle={canManage:boolean;academicYears:AcademicYear[]};
type GradeRow={
  gradeLevel:string;studentCount:number;classCount:number;assessmentAssigned:number;assessmentCompleted:number;
  participationCompleted:number;verifiedEvidence:number;counselingCompleted:number;activeBadges:number;
};
type ClassRow={
  classGroupId:string;name:string;gradeLevel:string;classLabel:string;studentCount:number;
  assessmentCompleted:number;participationCompleted:number;verifiedEvidence:number;counselingCompleted:number;activeBadges:number;
};
type BadgeRow={badgeKey:string;name:string;category:string;issuedCount:number;activeCount:number};
type ReportTemplate={
  templateId:string;templateKey:string;name:string;audience:string;versionId:string;versionNumber:number;
  versionLabel:string;title:string;subtitle?:string|null;footer?:string|null;sections:string[];
  branding:Record<string,unknown>;publishedAt?:string|null;
};
type ReportPack={
  generatedAt:string;
  school:{schoolId:string;name:string;code?:string|null};
  academicYear:{academicYearId:string;yearCode:string;startsOn:string;endsOn:string;status:string};
  summary:{studentCount:number;classCount:number;programCount:number;assessmentAssigned:number;assessmentCompleted:number;participationCompleted:number;verifiedEvidence:number;counselingCompleted:number;badgeIssued:number;badgeActive:number;badgeClaimed:number};
  grades:GradeRow[];
  classes:ClassRow[];
  badges:{issued:number;active:number;revoked:number;deliveryWaiting:number;claimed:number;byCategory:Array<{category:string;issuedCount:number;activeCount:number}>;byBadge:BadgeRow[]};
  programs:Array<{programId:string;title:string;programType:string;status:string;participantCount:number;completedCount:number}>;
  dataQuality:{studentsWithoutClass:number;classesWithoutStaff:number;badgeDeliveryWaiting:number};
  template:ReportTemplate;
};

function statusLabel(value:string){
  const map:Record<string,string>={DRAFT:"초안",SCHEDULED:"일정화",OPEN:"운영 중",CLOSED:"종료",CANCELLED:"취소"};
  return map[value] || value;
}
function fmt(value?:string|null){
  if(!value)return "-";
  return new Date(value).toLocaleString("ko-KR",{year:"numeric",month:"2-digit",day:"2-digit",hour:"2-digit",minute:"2-digit"});
}
function templateText(value:string|undefined|null,pack:ReportPack){
  return (value || "")
    .replaceAll("{{yearCode}}",pack.academicYear.yearCode)
    .replaceAll("{{schoolName}}",pack.school.name);
}
function hasSection(pack:ReportPack,key:string){
  return pack.template?.sections?.includes(key) ?? true;
}
function csvCell(value:unknown){
  const text=value==null?"":String(value);
  return '"'+text.replace(/"/g,'""')+'"';
}
function downloadCsv(rows:Array<Record<string,unknown>>,name:string){
  if(!rows.length)return;
  const headers=Array.from(new Set(rows.flatMap((row)=>Object.keys(row))));
  const body=[headers.map(csvCell).join(","),...rows.map((row)=>headers.map((key)=>csvCell(row[key])).join(","))].join("\r\n");
  const blob=new Blob(["\uFEFF",body],{type:"text/csv;charset=utf-8"});
  const url=URL.createObjectURL(blob);
  const anchor=document.createElement("a");anchor.href=url;anchor.download=name;anchor.click();URL.revokeObjectURL(url);
}

export function CenterReportsPage({client,schoolId}:{client:PlatformClient;schoolId:string}){
  const [tab,setTab]=useState<Tab>("PROGRAMS");
  const [programs,setPrograms]=useState<ProgramReport|null>(null);
  const [lifecycle,setLifecycle]=useState<Lifecycle|null>(null);
  const [pack,setPack]=useState<ReportPack|null>(null);
  const [templates,setTemplates]=useState<ReportTemplate[]>([]);
  const [templateVersionId,setTemplateVersionId]=useState("");
  const [yearId,setYearId]=useState("");
  const [loadingPack,setLoadingPack]=useState(false);
  const [aiNarrative,setAiNarrative]=useState<CenterAiSection[]>([]);
  const [includeAiNarrative,setIncludeAiNarrative]=useState(false);
  const [error,setError]=useState("");

  useEffect(()=>{
    let active=true;
    setError("");
    Promise.all([
      client.rpc("center_program_report",{p_school_id:schoolId}),
      client.rpc("center_student_lifecycle_workspace",{p_school_id:schoolId}),
      client.rpc("center_report_template_catalog",{p_school_id:schoolId})
    ]).then(([reportResult,lifeResult,templateResult])=>{
      if(!active)return;
      const first=reportResult.error || lifeResult.error || templateResult.error;
      if(first){setError(first.message);return;}
      setPrograms(reportResult.data as ProgramReport);
      const life=lifeResult.data as Lifecycle;
      setLifecycle(life);
      const current=life.academicYears.find((item)=>item.status==="ACTIVE") || life.academicYears[0];
      setYearId((value)=>value || current?.academicYearId || "");
      const nextTemplates=(templateResult.data || []) as ReportTemplate[];
      setTemplates(nextTemplates);
      setTemplateVersionId((value)=>nextTemplates.some((item)=>item.versionId===value) ? value : nextTemplates[0]?.versionId || "");
    });
    return()=>{active=false;};
  },[client,schoolId]);

  useEffect(()=>{
    if(!lifecycle?.canManage || !yearId){setPack(null);return;}
    let active=true;
    setLoadingPack(true);
    client.rpc("center_school_report_pack_v2",{
      p_school_id:schoolId,
      p_academic_year_id:yearId,
      p_template_version_id:templateVersionId || null
    }).then(({data,error})=>{
      if(!active)return;
      setLoadingPack(false);
      if(error){setError(error.message);setPack(null);return;}
      setPack(data as ReportPack);
    });
    return()=>{active=false;};
  },[client,schoolId,lifecycle?.canManage,yearId,templateVersionId]);

  useEffect(()=>{
    setAiNarrative([]);
    setIncludeAiNarrative(false);
  },[schoolId,yearId,templateVersionId]);

  const yearOptions=useMemo(()=>lifecycle?.academicYears || [],[lifecycle]);
  if(error)return <p className="error">{error}</p>;
  if(!programs || !lifecycle)return <p className="muted">학교 운영 보고서를 불러오는 중…</p>;

  function exportGrades(){
    if(!pack)return;
    downloadCsv(pack.grades.map((row)=>({
      학년:row.gradeLevel,학생수:row.studentCount,학급수:row.classCount,
      검사배정:row.assessmentAssigned,검사완료:row.assessmentCompleted,
      활동완료:row.participationCompleted,검증결과물:row.verifiedEvidence,
      상담완료:row.counselingCompleted,유효배지:row.activeBadges
    })),`RS_CAREER_${pack.school.name}_${pack.academicYear.yearCode}_학년비교.csv`);
  }
  function exportClasses(){
    if(!pack)return;
    downloadCsv(pack.classes.map((row)=>({
      학급:row.name,학년:row.gradeLevel,반:row.classLabel,학생수:row.studentCount,
      검사완료:row.assessmentCompleted,활동완료:row.participationCompleted,
      검증결과물:row.verifiedEvidence,상담완료:row.counselingCompleted,유효배지:row.activeBadges
    })),`RS_CAREER_${pack.school.name}_${pack.academicYear.yearCode}_학급비교.csv`);
  }
  function exportBadges(){
    if(!pack)return;
    downloadCsv(pack.badges.byBadge.map((row)=>({
      배지:row.name,카테고리:row.category,발급:row.issuedCount,유효:row.activeCount
    })),`RS_CAREER_${pack.school.name}_${pack.academicYear.yearCode}_배지통계.csv`);
  }

  return (
    <section className="center-report-page report-erp06">
      <div className="section-heading report-no-print">
        <div><p className="eyebrow">리포트</p><h2>학교 진로교육 운영·성과 리포트</h2><p className="route-description">프로그램 운영부터 학년·학급 비교, 디지털배지 성과와 교육청 제출용 집계까지 관리합니다.</p></div>
        {lifecycle.canManage && (
          <div className="report-heading-actions">
            <select className="report-year-select" value={yearId} onChange={(e)=>setYearId(e.target.value)}>
              {yearOptions.map((year)=><option key={year.academicYearId} value={year.academicYearId}>{year.yearCode} · {year.status}</option>)}
            </select>
            <select className="report-template-select" value={templateVersionId} onChange={(e)=>setTemplateVersionId(e.target.value)}>
              {templates.map((item)=><option key={item.versionId} value={item.versionId}>{item.name} · {item.versionLabel}</option>)}
            </select>
          </div>
        )}
      </div>

      <nav className="report-tabs report-no-print">
        <button className={tab==="PROGRAMS"?"active":""} onClick={()=>setTab("PROGRAMS")}>프로그램</button>
        {lifecycle.canManage && <button className={tab==="COMPARISON"?"active":""} onClick={()=>setTab("COMPARISON")}>학년·학급 비교</button>}
        {lifecycle.canManage && <button className={tab==="BADGES"?"active":""} onClick={()=>setTab("BADGES")}>디지털배지 통계</button>}
        {lifecycle.canManage && <button className={tab==="OFFICIAL"?"active":""} onClick={()=>setTab("OFFICIAL")}>교육청 보고</button>}
      </nav>

      {tab==="PROGRAMS" && (
        <>
          <div className="report-summary-grid">
            <ReportMetric label="전체 프로그램" value={programs.summary.programCount}/>
            <ReportMetric label="운영 중" value={programs.summary.activeProgramCount}/>
            <ReportMetric label="도달 학생" value={programs.summary.learnerReach}/>
            <ReportMetric label="활동 완료" value={programs.summary.completedParticipationCount}/>
            <ReportMetric label="검증 증빙" value={programs.summary.verifiedEvidenceCount}/>
          </div>
          <div className="report-program-list">
            {programs.programs.map((program)=>(
              <article className="report-program-card" key={program.programId}>
                <header><div><p className="eyebrow">{program.yearCode} · {program.programType}</p><h3>{program.title}</h3><span>{program.targetGradeMin || "-"}~{program.targetGradeMax || "-"}학년 · {program.startsOn || "-"} ~ {program.endsOn || "-"}</span></div><span className={program.status==="OPEN"?"status-pill good":"status-pill"}>{statusLabel(program.status)}</span></header>
                <div className="report-program-metrics">
                  <ReportMetric label="참가 학생" value={program.participantCount} compact/>
                  <ReportMetric label="등록 건" value={program.registrationCount} compact/>
                  <ReportMetric label="출석" value={program.attendedCount} compact/>
                  <ReportMetric label="완료" value={program.completedCount} compact/>
                  <ReportMetric label="결석" value={program.absentCount} compact/>
                  <ReportMetric label="완료율" value={program.completionRate+"%"} compact/>
                  <ReportMetric label="증빙 생성" value={program.evidenceCreatedCount} compact/>
                  <ReportMetric label="증빙 검증" value={program.evidenceVerifiedCount} compact/>
                  <ReportMetric label="증빙 검증률" value={program.evidenceVerificationRate+"%"} compact/>
                </div>
              </article>
            ))}
            {!programs.programs.length && <p className="case-empty">집계할 프로그램이 없습니다.</p>}
          </div>
        </>
      )}

      {lifecycle.canManage && loadingPack && tab!=="PROGRAMS" && <p className="muted">학교 집계 리포트를 생성하는 중…</p>}

      {tab==="COMPARISON" && pack && (
        <div className="report-comparison-stack">
          <section className="official-report-card">
            <div className="panel-title"><strong>학년 비교</strong><button className="text-button" onClick={exportGrades}>CSV</button></div>
            <div className="report-data-table grade">
              <div className="report-data-row head"><span>학년</span><span>학생</span><span>학급</span><span>검사</span><span>활동</span><span>결과물</span><span>상담</span><span>배지</span></div>
              {pack.grades.map((row)=><div className="report-data-row" key={row.gradeLevel}><strong>{row.gradeLevel}학년</strong><span>{row.studentCount}</span><span>{row.classCount}</span><span>{row.assessmentCompleted}/{row.assessmentAssigned}</span><span>{row.participationCompleted}</span><span>{row.verifiedEvidence}</span><span>{row.counselingCompleted}</span><span>{row.activeBadges}</span></div>)}
            </div>
          </section>
          <section className="official-report-card">
            <div className="panel-title"><strong>학급 비교</strong><button className="text-button" onClick={exportClasses}>CSV</button></div>
            <div className="report-data-table class">
              <div className="report-data-row head"><span>학급</span><span>학생</span><span>검사완료</span><span>활동완료</span><span>결과물</span><span>상담</span><span>배지</span></div>
              {pack.classes.map((row)=><div className="report-data-row" key={row.classGroupId}><strong>{row.name}</strong><span>{row.studentCount}</span><span>{row.assessmentCompleted}</span><span>{row.participationCompleted}</span><span>{row.verifiedEvidence}</span><span>{row.counselingCompleted}</span><span>{row.activeBadges}</span></div>)}
            </div>
          </section>
        </div>
      )}

      {tab==="BADGES" && pack && (
        <div className="badge-report-stack">
          <div className="report-summary-grid badge">
            <ReportMetric label="누적 발급" value={pack.badges.issued}/>
            <ReportMetric label="유효" value={pack.badges.active}/>
            <ReportMetric label="회수" value={pack.badges.revoked}/>
            <ReportMetric label="수령 완료" value={pack.badges.claimed}/>
            <ReportMetric label="수령 대기·오류" value={pack.badges.deliveryWaiting}/>
          </div>
          <section className="official-report-card">
            <div className="panel-title"><strong>배지별 발급 현황</strong><button className="text-button" onClick={exportBadges}>CSV</button></div>
            <div className="badge-stat-grid">{pack.badges.byBadge.map((row)=><article key={row.badgeKey}><span>{row.category}</span><strong>{row.name}</strong><dl><div><dt>발급</dt><dd>{row.issuedCount}</dd></div><div><dt>유효</dt><dd>{row.activeCount}</dd></div></dl></article>)}</div>
          </section>
        </div>
      )}

      {tab==="OFFICIAL" && pack && (
        <>
          <CenterReportAiPanel
            client={client}
            schoolId={schoolId}
            academicYearId={yearId}
            templateVersionId={templateVersionId}
            value={aiNarrative}
            onChange={setAiNarrative}
          />
          {aiNarrative.length>0 && (
            <label className="report-no-print center-ai-report-include">
              <input type="checkbox" checked={includeAiNarrative} onChange={(e)=>setIncludeAiNarrative(e.target.checked)} />
              <span><strong>AI 서술문을 인쇄·PDF 보고서에 포함</strong><small>교사가 내용을 검토·수정한 뒤 선택하세요.</small></span>
            </label>
          )}
          <CenterReportSubmissionPanel client={client} schoolId={schoolId} academicYearId={yearId} templateVersionId={templateVersionId} />
          <div className="official-report-actions report-no-print"><button className="primary" onClick={()=>window.print()}>인쇄 / PDF 저장</button><button className="secondary" onClick={exportGrades}>학년 부속 CSV</button><button className="secondary" onClick={exportClasses}>학급 부속 CSV</button><button className="secondary" onClick={exportBadges}>배지 부속 CSV</button></div>
          <article className="official-report-document">
            <header className="official-cover">
              <p>{pack.template.name} · {pack.template.versionLabel}</p>
              <h1>{templateText(pack.template.title,pack)}</h1>
              <h2>{templateText(pack.template.subtitle || "{{schoolName}}",pack)}</h2>
              <div className="official-template-meta"><span>Template {pack.template.templateKey}</span><span>Version {pack.template.versionLabel}</span></div>
              <dl><div><dt>운영기간</dt><dd>{pack.academicYear.startsOn} ~ {pack.academicYear.endsOn}</dd></div><div><dt>생성일시</dt><dd>{fmt(pack.generatedAt)}</dd></div></dl>
            </header>

            {hasSection(pack,"SUMMARY") && <section className="official-section">
              <h3>1. 운영 개요</h3>
              <div className="official-summary-grid">
                <ReportMetric label="학생" value={pack.summary.studentCount}/>
                <ReportMetric label="학급" value={pack.summary.classCount}/>
                <ReportMetric label="프로그램" value={pack.summary.programCount}/>
                <ReportMetric label="검사 완료" value={pack.summary.assessmentCompleted}/>
                <ReportMetric label="활동 완료" value={pack.summary.participationCompleted}/>
                <ReportMetric label="검증 결과물" value={pack.summary.verifiedEvidence}/>
                <ReportMetric label="상담 완료" value={pack.summary.counselingCompleted}/>
                <ReportMetric label="배지 발급" value={pack.summary.badgeIssued}/>
                <ReportMetric label="배지 수령" value={pack.summary.badgeClaimed}/>
              </div>
            </section>}

            {hasSection(pack,"GRADES") && <section className="official-section">
              <h3>2. 학년별 운영 현황</h3>
              <div className="report-data-table grade print">
                <div className="report-data-row head"><span>학년</span><span>학생</span><span>검사</span><span>활동</span><span>결과물</span><span>상담</span><span>유효배지</span></div>
                {pack.grades.map((row)=><div className="report-data-row" key={row.gradeLevel}><strong>{row.gradeLevel}학년</strong><span>{row.studentCount}</span><span>{row.assessmentCompleted}/{row.assessmentAssigned}</span><span>{row.participationCompleted}</span><span>{row.verifiedEvidence}</span><span>{row.counselingCompleted}</span><span>{row.activeBadges}</span></div>)}
              </div>
            </section>}

            {hasSection(pack,"PROGRAMS") && <section className="official-section">
              <h3>3. 프로그램 운영</h3>
              <div className="official-program-table">
                {pack.programs.map((program)=><div key={program.programId}><span><strong>{program.title}</strong><small>{program.programType} · {program.status}</small></span><span>참여 {program.participantCount}</span><span>완료 {program.completedCount}</span></div>)}
                {!pack.programs.length && <p>운영 프로그램 없음</p>}
              </div>
            </section>}

            {hasSection(pack,"BADGES") && <section className="official-section">
              <h3>4. 디지털배지 운영</h3>
              <p className="official-narrative">총 {pack.badges.issued}건을 발급했고 현재 유효 {pack.badges.active}건, 학생 수령 완료 {pack.badges.claimed}건입니다. 수령 대기 또는 전달 문제는 {pack.badges.deliveryWaiting}건입니다.</p>
              <div className="badge-category-summary">{pack.badges.byCategory.map((row)=><span key={row.category}><b>{row.category}</b> 발급 {row.issuedCount} · 유효 {row.activeCount}</span>)}</div>
            </section>}

            {hasSection(pack,"QUALITY") && <section className="official-section">
              <h3>5. 운영 데이터 점검</h3>
              <div className="official-quality-grid">
                <ReportMetric label="학급 미연결 학생" value={pack.dataQuality.studentsWithoutClass}/>
                <ReportMetric label="담당교사 미배정 학급" value={pack.dataQuality.classesWithoutStaff}/>
                <ReportMetric label="배지 수령 대기·오류" value={pack.dataQuality.badgeDeliveryWaiting}/>
              </div>
            </section>}

            {includeAiNarrative && aiNarrative.length>0 && (
              <section className="official-section center-ai-report-print">
                <h3>6. 운영 서술 및 다음 과제</h3>
                {aiNarrative.map((section)=>(
                  <section key={section.key}><h4>{section.title}</h4><p>{section.body}</p></section>
                ))}
              </section>
            )}

            <footer className="official-report-footer">{templateText(pack.template.footer || "본 보고서는 RS CAREER ERP의 해당 학년도 운영 데이터를 기준으로 생성되었습니다.",pack)}</footer>
          </article>
        </>
      )}
    </section>
  );
}

function ReportMetric({label,value,compact=false}:{label:string;value:number|string;compact?:boolean}){
  return <div className={compact?"report-metric compact":"report-metric"}><span>{label}</span><strong>{value}</strong></div>;
}
